import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import AdminNavBar from "../components/AdminNavbar";
import "../assets/css/programmebiodata.css";

const isProduction = process.env.NODE_ENV === "production";
const API_BASE_URL = isProduction
  ? "https://schools.luth.gov.ng"
  : "http://localhost:3003";

const PAGE_SIZE = 8;
const STATUS_OPTIONS = ["Paid", "Pending"];

export default function ApplicantPayments() {
  const [data, setData] = useState([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [programmeFilter, setProgrammeFilter] = useState("All");

  // Edit state
  const [editing, setEditing] = useState(null);
  const [loadingEdit, setLoadingEdit] = useState(false);

  useEffect(() => {
    fetchPayments();
  }, []);

  async function fetchPayments() {
    try {
      const res = await fetch(`${API_BASE_URL}/api/checkapplicationinfo`);
      const json = await res.json();
      setData(Array.isArray(json) ? json : json.data || []);
    } catch (err) {
      console.error("Failed to fetch payments:", err);
    }
  }

  const programmes = [...new Set(data.map((d) => d.programme))].sort();

  // Filters
  const filtered = data.filter((item) => {
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      if (
        !(
          item.fullName.toLowerCase().includes(term) ||
          item.email.toLowerCase().includes(term)
        )
      )
        return false;
    }
    if (statusFilter !== "All" && item.paymentStatus !== statusFilter)
      return false;
    if (programmeFilter !== "All" && item.programme !== programmeFilter)
      return false;
    return true;
  });

  const start = pageIndex * PAGE_SIZE;
  const paged = filtered.slice(start, start + PAGE_SIZE);
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);

  function handleSearch() {
    setSearchTerm(searchInput.trim());
    setPageIndex(0);
  }

  function exportToExcel() {
    const ws = XLSX.utils.json_to_sheet(filtered);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Payments");
    XLSX.writeFile(wb, `ApplicantPayments.xlsx`);
  }

  // Open edit modal
  async function openEdit(item) {
    setLoadingEdit(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/checkapplicationinfo/${item.id}`
      );
      if (!res.ok) throw new Error("Load error");
      const full = await res.json();
      setEditing(full);
    } catch (err) {
      console.error(err);
      alert("Failed to load record for editing.");
    } finally {
      setLoadingEdit(false);
    }
  }

  function cancelEdit() {
    setEditing(null);
  }

  // Save edited record
  async function saveEdit() {
    if (!editing) return;
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/checkapplicationinfo/${editing.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(editing),
        }
      );
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Update failed");
      }
      setEditing(null);
      fetchPayments();
    } catch (err) {
      console.error(err);
      alert(`Save failed: ${err.message}`);
    }
  }

  return (
    <div style={{ marginTop: "-80px" }}>
      <AdminNavBar />
      <div className="programme-biodata-page">
        <main className="content">
          <div className="toolbar">
            <h1>Applicant Payments</h1>
            {/* Filters and Export */}
            <div
              style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}
            >
              {["All", ...STATUS_OPTIONS].map((status) => (
                <button
                  key={status}
                  onClick={() => {
                    setStatusFilter(status);
                    setPageIndex(0);
                  }}
                  style={{
                    padding: "0.5rem 0.75rem",
                    background:
                      status === "Paid"
                        ? "#4caf50"
                        : status === "Pending"
                        ? "#ff9800"
                        : "#888",
                    color: "#fff",
                    border: "none",
                    borderRadius: "4px",
                    opacity: statusFilter === status ? 1 : 0.7,
                    cursor: "pointer",
                  }}
                >
                  {status}
                </button>
              ))}
              <select
                value={programmeFilter}
                onChange={(e) => {
                  setProgrammeFilter(e.target.value);
                  setPageIndex(0);
                }}
                style={{
                  padding: "0.5rem",
                  borderRadius: "4px",
                  border: "1px solid #ccc",
                }}
              >
                <option value="All">All Programmes</option>
                {programmes.map((prog) => (
                  <option key={prog} value={prog}>
                    {prog}
                  </option>
                ))}
              </select>
              <input
                type="text"
                placeholder="Search name or email"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                style={{
                  padding: "0.5rem",
                  borderRadius: "4px",
                  border: "1px solid #ccc",
                }}
              />
              <button className="excel-btn" onClick={handleSearch}>
                Search
              </button>
              <button className="excel-btn" onClick={exportToExcel}>
                Export to Excel
              </button>
            </div>
          </div>

          <table className="biodata-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Full Name</th>
                <th>Phone</th>
                <th>Email</th>
                <th>Programme</th>
                <th>Payment Status</th>
                <th>Created At</th>
                <th>App No</th>
                <th>RRR</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paged.map((item) => (
                <tr key={item.id}>
                  <td>{item.id}</td>
                  <td>{item.fullName}</td>
                  <td>{item.phone}</td>
                  <td>{item.email}</td>
                  <td>{item.programme}</td>
                  <td>{item.paymentStatus}</td>
                  <td>{new Date(item.createdAt).toLocaleDateString()}</td>
                  <td>{item.ApplicationNo}</td>
                  <td>{item.RRR}</td>
                  <td>
                    <button onClick={() => openEdit(item)}>Edit</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="pagination">
            <button
              onClick={() => setPageIndex((i) => Math.max(0, i - 1))}
              disabled={pageIndex === 0}
            >
              Previous
            </button>
            <span>
              Page {pageIndex + 1} of {totalPages || 1}
            </span>
            <button
              onClick={() =>
                setPageIndex((i) => Math.min(totalPages - 1, i + 1))
              }
              disabled={pageIndex >= totalPages - 1}
            >
              Next
            </button>
          </div>

          {/* Edit Modal */}
          {editing && (
            <div className="overlay">
              <div className="modal edit-modal">
                {loadingEdit ? (
                  <div className="spinner">Loading…</div>
                ) : (
                  <>
                    <h2>Edit Payment</h2>
                    <div className="modal-grid">
                      {[
                        "fullName",
                        "phone",
                        "email",
                        "programme",
                        "paymentStatus",
                        "ApplicationNo",
                        "RRR",
                      ].map((field) => (
                        <div className="field" key={field}>
                          <label>{field.replace(/([A-Z])/g, " $1")}</label>
                          <input
                            type={field === "email" ? "email" : "text"}
                            value={editing[field] || ""}
                            onChange={(e) =>
                              setEditing({
                                ...editing,
                                [field]: e.target.value,
                              })
                            }
                          />
                        </div>
                      ))}
                    </div>
                    <div className="modal-actions">
                      <button onClick={saveEdit} className="btn save">
                        Save
                      </button>
                      <button onClick={cancelEdit} className="btn cancel">
                        Cancel
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
